import { env } from './env.js';

/**
 * Error reporting, loaded on demand.
 *
 * Sentry is roughly 45 KB gzipped. Importing it statically would put it in the
 * entry bundle of every visitor including the ones who never hit an error, and
 * the PRD budgets the storefront entry at 200 KB. So it is dynamically imported
 * the first time it is actually needed, and never at all when no DSN is set.
 */

let clientPromise = null;

const loadClient = () => {
  if (!env.sentryDsn) return null;

  clientPromise =
    clientPromise ??
    import('@sentry/react').then((Sentry) => {
      Sentry.init({
        dsn: env.sentryDsn,
        environment: env.isProduction ? 'production' : 'development',
        tracesSampleRate: env.isProduction ? 0.1 : 0,
        sendDefaultPii: false,
        beforeSend(event) {
          // A checkout form carries an address and a phone number. Neither
          // belongs in an error report.
          if (event.request) {
            delete event.request.data;
            delete event.request.cookies;
          }
          return event;
        },
        ignoreErrors: ['ResizeObserver loop', 'Non-Error promise rejection captured'],
      });
      return Sentry;
    });

  return clientPromise;
};

/** Starts the load early without blocking the first paint. */
export const initReporting = () => {
  if (!env.sentryDsn) return false;
  // Deferred to idle so it never competes with the first render.
  const start = () => loadClient();
  if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 4000 });
  else setTimeout(start, 2000);
  return true;
};

export const captureException = async (error, context) => {
  const pending = loadClient();
  if (!pending) {
    if (!env.isProduction) console.error(error, context);
    return;
  }
  const Sentry = await pending;
  Sentry.captureException(error, context ? { extra: context } : undefined);
};

export default captureException;
